/* istanbul ignore file */
import React from 'react';
import { batch } from 'react-redux';
import { parseLocaleSetCookieHeaders } from '@noths/polaris-client-localisation';
import { isContentPreviewEnabled, NotFound } from '@noths/polaris-client-next-components';
import type { Router } from '@noths/polaris-client-next-redux';
import { DepartmentBanner } from '@noths/polaris-client-ribbons-design-system';
import { getMvtStrategyFromRequest } from '@noths/polaris-client-user-configuration';
import { generateRandomString } from '@noths/polaris-client-utils';
import { type CurrencyCode, StatusCode } from '@noths/polaris-dev-ts-types';
import type { InternalLinkGroups } from '@noths/polaris-internal-links';
import type { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import Head from 'next/head';
import { parse } from 'url';

import { adaptCategoryQueryContentToStore } from 'src/adapters/contentToStore/adaptCategoryQueryContentToStore';
import { adaptPageContentToStore } from 'src/adapters/contentToStore/adaptPageContentToStore';
import { adaptPageSettingsToStore } from 'src/adapters/contentToStore/adaptPageSettingsToStore';
import {
  adaptStoreToNextPageURL,
  adaptStoreToPreviousPageURL,
} from 'src/adapters/storeToPageQueryString/adaptStoreToPaginationURL';
import { adaptStoreToCategoryPageLinkedData } from 'src/adapters/storeToSEO/adaptStoreToCategoryPageLinkedData';
import type { CategoryListingProps } from 'src/components/layouts/CategoryListing/CategoryListing';
import { CategoryListingContainer } from 'src/components/layouts/CategoryListing/CategoryListingContainer';
import { getFeatureFlagsWithMatchers } from 'src/featureFlags/getFeatureFlagsWithMatchers';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { actions as navigationActions } from 'src/redux/navigation/slice';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import type { ThunkSerializableError } from 'src/redux/products/thunks/thunkErrors';
import {
  CATEGORY_NOT_FOUND_ERROR,
  MISSING_CATEGORY_ID_ERROR,
  MISSING_LISTINGS_QUERY_ERROR,
  PRODUCTS_LIST_DATA_FETCHING_ERROR,
  serializeError,
} from 'src/redux/products/thunks/thunkErrors';
import { getInitialListingsControlsState } from 'src/redux/productsQuery/helpers/getInitialListingsControlsState';
import { selectPagePropsForClientHydration } from 'src/redux/selectors';
import { getStore } from 'src/redux/store';
import { actions as userActions } from 'src/redux/user/slice';
import { getLDBreadcrumbListSchema } from 'src/seo/linkedData/getLDBreadcrumbListSchema';
import { getLDItemListSchema } from 'src/seo/linkedData/getLDItemListSchema';
import { fetchGlobalContent } from 'src/services/contentstack/fetchGlobalContent';
import { fetchPLPContent } from 'src/services/contentstack/fetchPLPContent';
import { fetchPLPSettings } from 'src/services/contentstack/fetchPLPSettings';
import { getContentstackEnvironment } from 'src/services/contentstack/getContentstackEnvironment';
import { getInternalLinksForCLP } from 'src/services/internalLinks/getInternalLinksForCLP';
import { getBrowseServingConfig } from 'src/services/listings-api/servingConfigs';
import { fetchDeliveryEvent } from 'src/services/mononoth/fetchDeliveryEvent';
import { getServerSidePageEventObject } from 'src/tracking/events/pageEvent';
import { PageType } from 'src/types/navigation';
import type { NextConfig } from 'src/types/nextConfig';
import { adaptBreadcrumbsLinks } from 'src/utils/adaptBreadcrumbsLinks';
import { buildCategoryPageTitle } from 'src/utils/buildCategoryPageTitle';
import { getRobotsContent } from 'src/utils/getRobotsContent';
import { getPageFromParam } from 'src/utils/queryParams';
import { logger } from 'src/utils/serverLogger';
import { urlPathToVertexPageCategory } from 'src/utils/vertex';

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig() as NextConfig;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query, req, res } = context;
  const { category, ...queryParams } = query;
  const preview = isContentPreviewEnabled(context.req);
  const featureFlags = getFeatureFlagsWithMatchers(req);
  const pathname = parse(req.url!).pathname!;
  const env = getContentstackEnvironment(preview);
  const routerState: Router = { pathname, query: queryParams };
  const healthCheck = req.headers['health-check'] === 'true';
  const { _ga: googleAnalyticsVisitorId, cognitoId, currency, delivery_zone } = req.cookies;
  const { currency: initialCurrency, deliveryZone: initialDeliveryZone } =
    parseLocaleSetCookieHeaders(res.getHeaders());
  const currencyCode = (initialCurrency ?? currency) as CurrencyCode;
  const gaVisitorId = googleAnalyticsVisitorId ?? generateRandomString(10);

  const [[siteChromeContent], content, settings, deliveryEvent] = await Promise.all([
    fetchGlobalContent({ featureFlags, urlPath: pathname }),
    healthCheck
      ? import('src/services/contentstack/fixtures/healthCheckContentFixture').then(
          (file) => file.healthCheckContent,
        )
      : fetchPLPContent({ urlPath: pathname, env }),
    fetchPLPSettings({ env }),
    fetchDeliveryEvent(),
  ]);

  if (!content) {
    res.statusCode = StatusCode.NotFound;

    return {
      props: {
        paginationTagValues: [],
        siteChromeContent,
        updatedReduxState: {
          content: {
            error: 'NO_CONTENT_FOR_CATEGORY_PAGE',
          },
        },
      },
    };
  }

  const redirectToURL = content.redirect_to?.navigationConnection?.edges[0]?.node?.url || null;

  if (redirectToURL) {
    return {
      redirect: {
        destination: redirectToURL,
        permanent: false,
      },
    };
  }

  const breadcrumbLinks = adaptBreadcrumbsLinks(
    content.ancestorsConnection?.edges || [],
    content.heading,
    pathname,
  );
  const { facets, sortOptions } = getInitialListingsControlsState(query, {
    deliveryEvent: deliveryEvent && {
      date: deliveryEvent.last_deliver_by,
      name: deliveryEvent.name,
    },
  });

  let error;
  let canonicalFilters = null;

  try {
    canonicalFilters = adaptCategoryQueryContentToStore(content, featureFlags);
  } catch (err) {
    error = err as ThunkSerializableError;
  }

  const servingConfig = getBrowseServingConfig(featureFlags);

  const { dispatch, getState } = getStore(
    {
      router: routerState,
    },
    {
      content: {
        category: adaptPageContentToStore(content, pathname),
        ...(settings ? adaptPageSettingsToStore(settings, pathname) : {}),
      },
      navigation: {
        pageType: PageType.Category,
      },
      ...(error ? { products: { error: serializeError(error), pages: {} } } : {}),
      productsQuery: {
        ...(canonicalFilters && { canonicalFilters }),
        facets,
        page: getPageFromParam(query),
        pageCategories: [urlPathToVertexPageCategory(pathname)],
        ...(servingConfig && { servingConfig }),
        sortOptions,
      },
      user: {
        bot: getMvtStrategyFromRequest(req) === 'bot',
        cognitoID: cognitoId || null,
        currencyCode,
        gaVisitorId,
      },
    },
  );

  batch(() => {
    dispatch(navigationActions.setPathname({ url: req.url ?? '', host: req.headers.host ?? '' }));
    dispatch(userActions.setDeliveryZone(initialDeliveryZone ?? delivery_zone));
    dispatch(userActions.setFeatureFlags(featureFlags));
  });

  const [internalLinks] = await Promise.all([
    getInternalLinksForCLP(pathname),
    !error ? dispatch(requestProducts({ healthCheck })) : null,
  ]);

  const state = getState();
  const { products } = state;

  if (products.error) {
    switch (products.error.name) {
      case CATEGORY_NOT_FOUND_ERROR.name:
      case PRODUCTS_LIST_DATA_FETCHING_ERROR.name:
        res.statusCode = StatusCode.NotFound;
        break;
      case MISSING_CATEGORY_ID_ERROR.name:
        res.statusCode = StatusCode.InternalServerError;
        logger.error('category ID missing for category listing page');
        break;
      case MISSING_LISTINGS_QUERY_ERROR.name:
        res.statusCode = StatusCode.InternalServerError;
        logger.error('listings_query missing for category listing page');
        break;
      default:
        res.statusCode = StatusCode.InternalServerError;
        res.setHeader('Retry-After', serverRuntimeConfig.retryAfterDuration);
        break;
    }
  }

  const pageEventData = getServerSidePageEventObject(
    publicRuntimeConfig.version,
    publicRuntimeConfig.appVersion,
    state,
  );

  const internalLinkGroups = internalLinks as InternalLinkGroups;
  const previousPageURL = adaptStoreToPreviousPageURL(state);
  const nextPageURL = adaptStoreToNextPageURL(state);
  const inspirationLinks = state.content.category?.inspirationPageLinks || [];
  const bottomLinks = inspirationLinks.concat(internalLinkGroups?.bottom || []);

  const props: Partial<CategoryListingProps> = {
    featureFlags,
    preview,
    siteChromeContent,
    // TODO: update buildCategoryPageTitle to not expect last breadcrumb entry to be current page
    pageTitle: buildCategoryPageTitle({
      heading: state.content.category!.heading,
      breadcrumbLinks,
      pageTitleOverride: state.content.category!.pageTitleOverride,
    }),
    paginationTagValues: [previousPageURL, nextPageURL],
    pageEventData,
    ...selectPagePropsForClientHydration(state),
    topLinks: internalLinkGroups?.top || [],
    bottomLinks,
    breadcrumbLinks,
    linkedDataSchema: adaptStoreToCategoryPageLinkedData(state),
  };

  return {
    props,
  };
};

const CategoryPage = (props: CategoryListingProps) => {
  const { content, productsQuery } = props.updatedReduxState as ReduxApplicationState;
  const [prevLinkHref, nextLinkHref] = props.paginationTagValues || [];
  const robotsContent = getRobotsContent(content.category?.indexable, productsQuery?.page || 1);

  return (
    <>
      <Head>
        <title>{props.pageTitle}</title>
        {content.category?.metaTagDescription && (
          <meta content={content.category.metaTagDescription} name="description" />
        )}
        {content.category?.canonicalTag && (
          <link href={content.category!.canonicalTag} rel="canonical" />
        )}
        {robotsContent && <meta content={robotsContent} name="robots" />}
        {prevLinkHref && <link href={prevLinkHref} rel="prev" />}
        {nextLinkHref && <link href={nextLinkHref} rel="next" />}
        {props.breadcrumbLinks && (
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(getLDBreadcrumbListSchema(props.breadcrumbLinks)),
            }}
            type="application/ld+json"
          />
        )}
        {props.linkedDataSchema && (
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(getLDItemListSchema(props.linkedDataSchema)),
            }}
            type="application/ld+json"
          />
        )}
      </Head>
      {content.departmentBanner && <DepartmentBanner {...content.departmentBanner} />}
      {content.error ? <NotFound /> : <CategoryListingContainer {...props} />}
    </>
  );
};

export default CategoryPage;
